import { useMutation, useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Field,
  Page,
  PageHeader,
  SearchSelect,
  Select,
  Switch,
  backLinkClasses,
} from '@evogard/ui';
import {
  BILLING_AWARD_BOLETO_CATEGORIES,
  BILLING_AWARD_BOLETO_CATEGORY_LABELS,
  BILLING_AWARD_KINDS,
  BILLING_AWARD_KIND_LABELS,
  billingAwardRuleProblem,
  fromColumn,
  parseMoneyInput,
  toColumn,
  type BillingAwardBoletoCategory,
  type BillingAwardKind,
} from '@evogard/domain';
import type { BillingAwardConfig, BillingAwardRuleWire } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { AWARDS_KEY, currentPeriod, ruleFromWire, useAwardTenants, useInvalidateAwards, type AwardTenant } from './useAwards.js';

type Operator = BillingAwardConfig['operators'][number];
type BoletoType = BillingAwardConfig['boletoTypes'][number];
interface TierDraft {
  kind: BillingAwardKind;
  label: string;
  minAmount: string;
  bonus: string;
}

/** Dinheiro no campo: "1.234,56" na tela, "1234.56" no fio. */
const moneyText = (wire: string) => toColumn(fromColumn(wire)!).replace('.', ',');

/**
 * A configuração da premiação: quem é operador e com qual código de usuário do
 * SGA em cada base, os tipos de boleto e de quem é cada um, as faixas e o que
 * está ligado.
 *
 * Operadores, tipos e departamentos são de cada base — o código do SGA de uma
 * pessoa é um na CE e outro na GO. A RÉGUA é uma só: a faixa vale sobre a soma
 * das bases, e "Guardar as faixas" grava a mesma nas duas.
 */
export function AwardsSettingsScreen() {
  const tenants = useAwardTenants();
  const configs = useQueries({
    queries: tenants.map((t) => ({
      queryKey: [...AWARDS_KEY, 'configuracao', t.slug],
      queryFn: async () =>
        ok(await api.billingAwards.config({ params: { tenant: t.slug } }), 200, `Não foi possível carregar a configuração da ${t.name}.`),
    })),
  });
  const views = useQueries({
    queries: tenants.map((t) => ({
      queryKey: [...AWARDS_KEY, 'competencia', t.slug, currentPeriod()],
      queryFn: async () =>
        ok(
          await api.billingAwards.period({ params: { tenant: t.slug }, query: { period: currentPeriod() } }),
          200,
          `Não foi possível carregar a premiação da ${t.name}.`,
        ),
    })),
  });
  const editable = tenants.filter((_, i) => views[i]?.data?.can.configure);
  const [slug, setSlug] = useState('');
  const tenant = tenants.find((t) => t.slug === slug) ?? editable[0] ?? tenants[0];
  const index = tenant ? tenants.indexOf(tenant) : -1;
  const config = index >= 0 ? configs[index]?.data : undefined;
  const canEdit = tenant !== undefined && editable.includes(tenant);

  const loaded = tenants.flatMap((t, i) => (configs[i]?.data ? [{ tenant: t, config: configs[i]!.data! }] : []));
  const ruleProblem =
    loaded.length > 1
      ? billingAwardRuleProblem(loaded.map((l) => ({ tenant: l.tenant.name, rule: ruleFromWire(l.config.rule) })))
      : null;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Configurar a premiação"
        back={
          <Link to="/billing/awards" className={backLinkClasses()}>
            Premiação da cobrança
          </Link>
        }
      />
      {configs.map((c, i) => (c.error ? <Alert key={tenants[i]!.slug} tone="erro">{c.error.message}</Alert> : null))}
      {views.length > 0 && views.every((v) => v.data) && editable.length === 0 && (
        <Alert tone="informacao">Seu grupo vê a configuração, mas não a muda: isso é de quem configura a premiação.</Alert>
      )}
      {ruleProblem && <Alert tone="erro">{ruleProblem} Guarde as faixas abaixo para igualar as bases.</Alert>}

      {tenants.length > 1 && (
        <div className="w-56">
          <Select label="Base" value={tenant?.slug ?? ''} onChange={(e) => setSlug(e.target.value)}>
            {tenants.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      )}

      {tenant && config && (
        <>
          <Operators key={`op-${tenant.slug}`} tenant={tenant} config={config} canEdit={canEdit} />
          <BoletoTypes key={`ty-${tenant.slug}`} tenant={tenant} config={config} canEdit={canEdit} />
          <Rule
            key={`ru-${tenant.slug}`}
            tenant={tenant}
            config={config}
            all={loaded.filter((l) => editable.includes(l.tenant))}
            canEdit={canEdit}
          />
        </>
      )}
    </Page>
  );
}

function Operators({ tenant, config, canEdit }: { tenant: AwardTenant; config: BillingAwardConfig; canEdit: boolean }) {
  const invalidate = useInvalidateAwards();
  const [rows, setRows] = useState<Operator[]>(config.operators);
  useEffect(() => setRows(config.operators), [config.operators]);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingAwards.saveOperators({
          params: { tenant: tenant.slug },
          body: { operators: rows.map(({ userId, erpUserCode }) => ({ userId, erpUserCode: erpUserCode.trim() })) },
        }),
        200,
        'Não foi possível guardar os operadores.',
      ),
    onSuccess: () => invalidate(),
  });
  const patch = (userId: string, p: Partial<Operator>) => setRows((rs) => rs.map((r) => (r.userId === userId ? { ...r, ...p } : r)));
  const candidates = config.candidates.filter((c) => !rows.some((r) => r.userId === c.id));
  const shape = (list: readonly Operator[]) => JSON.stringify(list.map((o) => [o.userId, o.erpUserCode.trim()]));
  const pending = shape(rows) !== shape(config.operators);

  return (
    <Card
      title={`Operadores — ${tenant.name}`}
      descricao="Quem recebe a premiação, e o código de usuário da pessoa NO SGA desta base: é por ele que o atendimento registrado no SGA vira dela. Quem sai da empresa para de atender e não casa mais nada, então pode ficar na lista. Tirar da lista tira das competências abertas; as fechadas guardam quem era operador naquele mês."
    >
      <div className="flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.userId} className="flex flex-wrap items-end gap-4">
            <span className="w-64 pb-2 text-sm font-medium">{r.name}</span>
            <div className="w-40">
              <Field
                label="Código no SGA"
                value={r.erpUserCode}
                disabled={!canEdit}
                onChange={(e) => patch(r.userId, { erpUserCode: e.target.value })}
              />
            </div>
            {canEdit && (
              <Button size="sm" variant="discreto" onClick={() => setRows((rs) => rs.filter((x) => x.userId !== r.userId))}>
                Tirar
              </Button>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-texto-suave">Nenhum operador nesta base.</p>}
        {canEdit && (
          <div className="w-80">
            <SearchSelect
              label="Adicionar operador"
              placeholder="Digite o nome…"
              searchPlaceholder="Buscar pelo nome…"
              emptyMessage="Ninguém com esse nome entre as contas ativas desta base."
              options={candidates.map((c) => ({ value: c.id, label: c.name }))}
              value=""
              onChange={(id) => {
                const c = candidates.find((x) => x.id === id);
                if (c) setRows((rs) => [...rs, { userId: c.id, name: c.name, erpUserCode: '' }]);
              }}
              help="A pessoa precisa de conta com acesso a esta base."
            />
          </div>
        )}
        {pending && <Alert tone="alerta">A lista mudou e ainda não foi guardada.</Alert>}
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        {save.isSuccess && !pending && <Alert tone="sucesso">Operadores guardados.</Alert>}
        {canEdit && (
          <div>
            <Button loading={save.isPending} disabled={rows.some((r) => r.erpUserCode.trim() === '')} onClick={() => save.mutate()}>
              Guardar os operadores
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function BoletoTypes({ tenant, config, canEdit }: { tenant: AwardTenant; config: BillingAwardConfig; canEdit: boolean }) {
  const invalidate = useInvalidateAwards();
  const [rows, setRows] = useState<BoletoType[]>(config.boletoTypes);
  useEffect(() => setRows(config.boletoTypes), [config.boletoTypes]);
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingAwards.saveBoletoTypes({
          params: { tenant: tenant.slug },
          /* o nome não vai: ele é o do SGA, lido do vocabulário do núcleo */
          body: { boletoTypes: rows.map(({ label: _label, ...t }) => ({ ...t, typeCode: t.typeCode.trim() })) },
        }),
        200,
        'Não foi possível guardar os tipos de boleto.',
      ),
    onSuccess: () => invalidate(),
  });
  const patch = (i: number, p: Partial<BoletoType>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const pending = JSON.stringify(rows) !== JSON.stringify(config.boletoTypes);
  const incomplete = rows.some((r) => r.typeCode.trim() === '');

  return (
    <Card
      title={`Tipos de boleto — ${tenant.name}`}
      descricao="O boleto só conta para o dono do tipo dele. O código é o do tipo de boleto no SGA desta base, e o nome é o que o SGA dá a ele — renomeado lá, muda aqui. Tipo sem dono, desligado ou que não conta fica cadastrado e não casa."
    >
      <div className="flex flex-col gap-3">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3 border-b border-borda pb-3">
            <div className="w-28">
              <Field label="Código" value={r.typeCode} disabled={!canEdit} onChange={(e) => patch(i, { typeCode: e.target.value })} />
            </div>
            <div className="min-w-56 flex-1 pb-2 text-sm">
              <span className="block text-xs text-texto-suave">Nome no SGA</span>
              {r.label ?? <span className="text-texto-suave">sem nome na lista do SGA para este código</span>}
            </div>
            <div className="w-48">
              <Select
                label="Categoria"
                value={r.category}
                disabled={!canEdit}
                onChange={(e) => patch(i, { category: e.target.value as BillingAwardBoletoCategory })}
              >
                {BILLING_AWARD_BOLETO_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {BILLING_AWARD_BOLETO_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-52">
              <Select
                label="Dono"
                value={r.userId ?? ''}
                disabled={!canEdit}
                onChange={(e) => patch(i, { userId: e.target.value || null })}
              >
                <option value="">Sem dono</option>
                {config.operators.map((o) => (
                  <option key={o.userId} value={o.userId}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </div>
            <Switch
              label={`Conta para a premiação — tipo ${r.typeCode}`}
              checked={r.countsForAward}
              disabled={!canEdit}
              onCheckedChange={(v) => patch(i, { countsForAward: v })}
              onLabel="Conta"
              offLabel="Não conta"
            />
            <Switch
              label={`Ativo — tipo ${r.typeCode}`}
              checked={r.active}
              disabled={!canEdit}
              onCheckedChange={(v) => patch(i, { active: v })}
              onLabel="Ativo"
              offLabel="Inativo"
            />
            {canEdit && (
              <Button size="sm" variant="discreto" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                Tirar
              </Button>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-texto-suave">Nenhum tipo de boleto nesta base.</p>}
        {pending && <Alert tone="alerta">Os tipos mudaram e ainda não foram guardados.</Alert>}
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        {save.isSuccess && !pending && <Alert tone="sucesso">Tipos guardados.</Alert>}
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="contorno"
              onClick={() =>
                setRows((rs) => [...rs, { typeCode: '', label: null, category: 'mensalidade', userId: null, countsForAward: true, active: true }])
              }
            >
              Adicionar tipo
            </Button>
            <Button loading={save.isPending} disabled={incomplete} onClick={() => save.mutate()}>
              Guardar os tipos
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function Rule({
  tenant,
  config,
  all,
  canEdit,
}: {
  tenant: AwardTenant;
  config: BillingAwardConfig;
  all: Array<{ tenant: AwardTenant; config: BillingAwardConfig }>;
  canEdit: boolean;
}) {
  const invalidate = useInvalidateAwards();
  const fromConfig = () => ({
    enabled: { ...config.rule.enabled },
    tiers: config.rule.tiers.map((t): TierDraft => ({ ...t, minAmount: moneyText(t.minAmount), bonus: moneyText(t.bonus) })),
  });
  const [draft, setDraft] = useState(fromConfig);
  const [departments, setDepartments] = useState(config.departmentCodes.join(', '));
  useEffect(() => {
    setDraft(fromConfig());
    setDepartments(config.departmentCodes.join(', '));
  }, [config]);

  const parsed = draft.tiers.map((t) => ({ ...t, min: parseMoneyInput(t.minAmount), value: parseMoneyInput(t.bonus) }));
  const invalid = parsed.some((t) => t.min === null || t.value === null || t.label.trim() === '');
  const rule = (): BillingAwardRuleWire => ({
    enabled: draft.enabled,
    tiers: parsed.map((t) => ({ kind: t.kind, label: t.label.trim(), minAmount: toColumn(t.min!), bonus: toColumn(t.value!) })),
  });
  const codes = departments
    .split(/[\s,;]+/)
    .map((c) => c.trim())
    .filter(Boolean);

  const save = useMutation({
    mutationFn: async () => {
      /* a régua vai para todas as bases que a pessoa configura; os departamentos são de cada uma */
      for (const target of all.length > 0 ? all : [{ tenant, config }]) {
        ok(
          await api.billingAwards.saveRule({
            params: { tenant: target.tenant.slug },
            body: { rule: rule(), departmentCodes: target.tenant.slug === tenant.slug ? codes : target.config.departmentCodes },
          }),
          200,
          `Não foi possível guardar as faixas na ${target.tenant.name}.`,
        );
      }
    },
    onSuccess: () => invalidate(),
  });
  const setTier = (i: number, p: Partial<TierDraft>) =>
    setDraft((d) => ({ ...d, tiers: d.tiers.map((t, j) => (j === i ? { ...t, ...p } : t)) }));

  return (
    <Card
      title="Faixas e metas"
      descricao={`A régua é uma só: vale sobre o recuperado SOMADO das bases, e guardar grava a mesma em ${(all.length > 0 ? all : [{ tenant }]).map((a) => a.tenant.name).join(' e ')}. A faixa paga é a de maior valor mínimo que o recuperado alcança. A competência fechada guarda a régua do dia do fechamento.`}
    >
      <div className="flex flex-col gap-5">
        {BILLING_AWARD_KINDS.map((kind) => (
          <div key={kind} className="flex flex-col gap-2">
            <Switch
              label={BILLING_AWARD_KIND_LABELS[kind]}
              checked={draft.enabled[kind]}
              disabled={!canEdit}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: { ...d.enabled, [kind]: v } }))}
              onLabel={`${BILLING_AWARD_KIND_LABELS[kind]} ligada`}
              offLabel={`${BILLING_AWARD_KIND_LABELS[kind]} desligada`}
            />
            {draft.tiers.map((t, i) =>
              t.kind !== kind ? null : (
                <div key={i} className="flex flex-wrap items-end gap-3 pl-4">
                  <div className="w-40">
                    <Field label="Faixa" value={t.label} disabled={!canEdit} onChange={(e) => setTier(i, { label: e.target.value })} />
                  </div>
                  <div className="w-40">
                    <Field
                      label="A partir de (R$)"
                      value={t.minAmount}
                      disabled={!canEdit}
                      onChange={(e) => setTier(i, { minAmount: e.target.value })}
                    />
                  </div>
                  <div className="w-40">
                    <Field label="Paga (R$)" value={t.bonus} disabled={!canEdit} onChange={(e) => setTier(i, { bonus: e.target.value })} />
                  </div>
                  {canEdit && (
                    <Button size="sm" variant="discreto" onClick={() => setDraft((d) => ({ ...d, tiers: d.tiers.filter((_, j) => j !== i) }))}>
                      Tirar
                    </Button>
                  )}
                </div>
              ),
            )}
            {canEdit && (
              <div className="pl-4">
                <Button
                  size="sm"
                  variant="contorno"
                  onClick={() => setDraft((d) => ({ ...d, tiers: [...d.tiers, { kind, label: '', minAmount: '', bonus: '' }] }))}
                >
                  Adicionar faixa
                </Button>
              </div>
            )}
          </div>
        ))}
        <div className="w-80">
          <Field
            label={`Departamentos do SGA — ${tenant.name}`}
            value={departments}
            disabled={!canEdit}
            onChange={(e) => setDepartments(e.target.value)}
            help="Os códigos de departamento de onde o atendimento conta, separados por vírgula. Na Premiação antiga: CE 23 e 30, GO 5 e 6."
          />
        </div>
        {invalid && <Alert tone="alerta">Toda faixa precisa de nome, valor mínimo e valor pago.</Alert>}
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        {save.isSuccess && <Alert tone="sucesso">Faixas guardadas.</Alert>}
        {canEdit && (
          <div>
            <Button loading={save.isPending} disabled={invalid || codes.length === 0} onClick={() => save.mutate()}>
              Guardar as faixas
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

